/**
 * Curated catalog slugs for commercial landing pages.
 * Source of truth remains `products` via ProductsBySlugsCarousel —
 * this file only selects which visible catalog rows to surface.
 *
 * Hidden / do-not-use (from docs/PRODUKTINVENTERING.md):
 * - lilla-studentflaket
 * - DJ-profilprodukter (dj-dj-rbnjo, dj-dj-alex-gator, dj-wiktor-c)
 *
 * Festival: d&b XSL/SL-GSUB are intentional premium/quote-only anchors.
 */

export type LandingProductSection = {
  title: string;
  subtitle?: string;
  slugs: readonly string[];
  ctaLink?: string;
  ctaLabel?: string;
  showAllInView?: boolean;
  testId: string;
};

/** Never surface on public landings. */
export const HIDDEN_LANDING_PRODUCT_SLUGS = [
  'lilla-studentflaket',
  'dj-dj-rbnjo',
  'dj-dj-alex-gator',
  'dj-wiktor-c',
] as const;

// --- Shared building blocks ---

export const FESTPAKET_LADDER_SLUGS = [
  'lilla-festpaketet',
  'standard-festpaket',
  'stora-festpaketet-10-000-w-ljud-maxad-ljusshow-total-partykansla',
] as const;

/** Compact geo mix: 5 products — not 3×3 carousels. */
export const GEO_LJUD_LJUS_SLUGS = [
  'standard-festpaket',
  'lilla-ljudpaketet',
  'mellan-ljudpaketet',
  'soundboks-gen-3-portabel-festhogtalare-med-extremt-tryck',
  'omgivningsbelysning-4-pack-uplights',
  'mellan-ljuspaketet-effekt-fargsattning',
] as const;

/** Complete stage solution (quote-only) — shared across scen-related landings. */
export const SCEN_OFFERT_PRODUCT_SLUG =
  'scener-scenpodier-tross-kravallstaket-offertbaserade-losningar' as const;

// --- Per-route sections ---

export const HYRA_LJUD_STUDENTFEST_PRODUCTS: LandingProductSection = {
  testId: 'studentfest-products',
  title: 'Populära ljudpaket för studentfest',
  subtitle: 'Aktuella paket från katalogen – se pris per dygn och gå vidare till bokning.',
  slugs: [
    'lilla-ljudpaketet',
    'soundboks-gen-3-portabel-festhogtalare-med-extremt-tryck',
    'mellan-ljudpaketet',
    'hogtalarpaket-10-000w',
    'standard-festpaket',
  ],
  ctaLink: '/produkter',
  ctaLabel: 'Se hela produktkatalogen',
};

export const STUDENTFLAK_LJUDPAKET_PRODUCTS: LandingProductSection = {
  testId: 'studentflak-products',
  title: 'Studentflakspaket i katalogen',
  subtitle: 'Publika flakpaket med ljud, bas och el – se pris och boka.',
  slugs: ['stora-studentflaket', 'studentflak-xxl'],
  ctaLink: '/student',
  ctaLabel: 'Se studentsidan',
  showAllInView: true,
};

export const JULFEST_FORETAG_PRODUCTS: LandingProductSection = {
  testId: 'julfest-products',
  title: 'Scen, proffs-PA och mikrofoner till julfesten',
  subtitle:
    'Samma klass som företags- och eventproduktion: offertbaserad scen, d&b och RCF-ljud samt Shure-mikrofoner. Primär väg är offert.',
  slugs: [
    SCEN_OFFERT_PRODUCT_SLUG,
    'd-b-xsl8',
    'd-b-xsl12',
    'd-b-sl-gsub',
    'rcf-hdl-6-a-kompakt-line-array',
    'premium-rcf-nxl-24-a-mk2-sub-18-ax-komplett-ljudsystem',
    'premium-rcf-art-912-a-dubbel-inkl-stativ',
    'rcf-sub-9029-as-professionell-dubbel-19-subwoofer',
    'shure-blx288-sm58-tradlost-mikrofonsystem-med-tva-handmikrofoner',
    'shure-blx14r-mx53-tradlost-headsetsystem',
    'shure-sm58-mikrofon-med-stativ-och-xlr-kabel',
    'kallgnistpaket-2-st-kallgnistmaskiner-sparkular-effekt',
  ],
  ctaLink: '/foretag',
  ctaLabel: 'Företagshubben',
};

export const EVENTTEKNIK_FORETAG_AUDIO_PRODUCTS: LandingProductSection = {
  testId: 'eventteknik-foretag-audio',
  title: 'Ljud & mikrofoner',
  subtitle: 'Konferensljud och trådlösa mikrofoner från katalogen.',
  slugs: [
    'konferenspaket-konferens-workshop-utbildning-eller-foretagsevent',
    'mellan-ljudpaketet',
    'headset-slipsmikrofon-duo-handsfree',
    'shure-blx288-sm58-tradlost-mikrofonsystem-med-tva-handmikrofoner',
    'd-b-xsl8',
  ],
  ctaLink: '/produkter',
  ctaLabel: 'Se hela katalogen',
};

export const EVENTTEKNIK_FORETAG_VISUAL_PRODUCTS: LandingProductSection = {
  testId: 'eventteknik-foretag-visual',
  title: 'Bild & presentation',
  subtitle: 'Projektor, duk och skärm för konferens och lansering.',
  slugs: [
    'full-hd-projektor-5-200-ansi-lumen',
    'projektor',
    'projektor-duk-120-100-eller-80',
    '4k-uhd-tv-55-professionell-bildvisning-for-event-och-konferenser',
  ],
  ctaLink: '/produkter',
  ctaLabel: 'Se bildutrustning',
  showAllInView: true,
};

/** Premium festival capacity: d&b line-array first, then complete PA / RCF / scen. */
export const EVENTTEKNIK_FESTIVAL_PRODUCTS: LandingProductSection = {
  testId: 'eventteknik-festival-products',
  title: 'Kapacitet för större event',
  subtitle:
    'd&b line-array och proffs-PA för festival och stora publikevent – lösningar som oftast paketeras i offert.',
  slugs: [
    'd-b-xsl8',
    'd-b-xsl12',
    'd-b-sl-gsub',
    'hogtalarpaket-10-000w',
    'rcf-art-915-a-rcf-sub-8008-as',
    SCEN_OFFERT_PRODUCT_SLUG,
  ],
  ctaLink: '/scen',
  ctaLabel: 'Se scen & tross',
};

export const EVENTTEKNIK_MASSOR_PRODUCTS: LandingProductSection = {
  testId: 'eventteknik-massor-products',
  title: 'Utrustning för mässa & monter',
  subtitle: 'Podier, tross, uplights och bild – anpassat för monter-intent.',
  slugs: [
    SCEN_OFFERT_PRODUCT_SLUG,
    'scenpodie-1x0-5-m',
    'tross-2-meter',
    'omgivningsbelysning-10-pack-uplights',
    'full-hd-projektor-5-200-ansi-lumen',
    'lilla-ljudpaketet',
  ],
  ctaLink: '/produkter',
  ctaLabel: 'Se hela katalogen',
};

export const LJUD_LJUS_BROLLOP_PRODUCTS: LandingProductSection = {
  testId: 'ljud-ljus-brollop-products',
  title: 'Populära paket för bröllop',
  subtitle: 'Bröllopspaket, uplights och mikrofoner från katalogen.',
  slugs: [
    'lilla-brollopspaket',
    'brollopspaket-classic',
    'brollopspaket-premium',
    'skraddarsytt-brollopspaket-skapa-er-dromdag',
    'omgivningsbelysning-4-pack-uplights',
    'tradlost-mikrofonset',
  ],
  ctaLink: '/brollop',
  ctaLabel: 'Se bröllopssidan',
};

/** Hub /brollop — alla publika bröllopspaket inklusive skräddarsytt (quote-only). */
export const BROLLOP_HUB_PRODUCTS: LandingProductSection = {
  testId: 'brollop-hub-products',
  title: 'Bröllopspaket',
  subtitle:
    'Ljud, ljus och stämning för vigsel, middag och fest – från intima tillställningar till skräddarsydda helhetslösningar.',
  slugs: [
    'lilla-brollopspaket',
    'brollopspaket-classic',
    'brollopspaket-premium',
    'skraddarsytt-brollopspaket-skapa-er-dromdag',
  ],
  ctaLink: '/produkter',
  ctaLabel: 'Se alla bröllopsprodukter',
  showAllInView: true,
};

export const LJUD_LJUS_SKANE_PRODUCTS: LandingProductSection = {
  testId: 'ljud-ljus-skane-products',
  title: 'Populära ljud- och ljuspaket',
  subtitle: 'Ett urval från katalogen för fest, bröllop och företag i Skåne.',
  slugs: GEO_LJUD_LJUS_SLUGS,
  ctaLink: '/produkter',
  ctaLabel: 'Se hela katalogen',
};

export const LJUD_LJUS_MALMO_PRODUCTS: LandingProductSection = {
  testId: 'ljud-ljus-malmo-products',
  title: 'Populära paket i Malmö',
  subtitle: 'Ljud, ljus och festpaket ni kan boka online – eller begära offert.',
  slugs: GEO_LJUD_LJUS_SLUGS,
  ctaLink: '/hyra-hogtalare-malmo',
  ctaLabel: 'Hyra högtalare Malmö',
};

export const LJUD_LJUS_LUND_PRODUCTS: LandingProductSection = {
  testId: 'ljud-ljus-lund-products',
  title: 'Populära paket i Lund',
  subtitle: 'Passar studentfest, nation och företag – aktuella priser i katalogen.',
  slugs: GEO_LJUD_LJUS_SLUGS,
  ctaLink: '/produkter',
  ctaLabel: 'Se hela katalogen',
};

export const LJUD_LJUS_HELSINGBORG_PRODUCTS: LandingProductSection = {
  testId: 'ljud-ljus-helsingborg-products',
  title: 'Populära paket i Helsingborg',
  subtitle: 'Ljud och ljus för bröllop och företagsevent i nordvästra Skåne.',
  slugs: GEO_LJUD_LJUS_SLUGS,
  ctaLink: '/produkter',
  ctaLabel: 'Se hela katalogen',
};

/** Stage-first: complete offer → podier → trappa. No loose tross bits. */
export const HYRA_SCEN_MALMO_PRODUCTS: LandingProductSection = {
  testId: 'hyra-scen-malmo-products',
  title: 'Scenuthyrning i Malmö',
  subtitle: 'Kompletta scenlösningar och podier – inte lösa komponenter först.',
  slugs: [
    SCEN_OFFERT_PRODUCT_SLUG,
    'scenpodie-1x0-5-m',
    'scenpodie-2x1-m',
    'scenpodie-2x0-5m',
    'scentrappa',
  ],
  ctaLink: '/scen',
  ctaLabel: 'Se alla scenprodukter',
  showAllInView: false,
};

/** Hub /scen — offertpaket först, sedan konkreta scenbyggstenar. */
export const SCEN_HUB_PRODUCTS: LandingProductSection = {
  testId: 'scen-hub-products',
  title: 'Scenprodukter att hyra',
  subtitle:
    'Börja med komplett scenlösning (offert) – eller välj podier, trappa och tross från katalogen.',
  slugs: [
    SCEN_OFFERT_PRODUCT_SLUG,
    'scenpodie-1x0-5-m',
    'scenpodie-2x1-m',
    'scentrappa',
    'tross-2-meter',
  ],
  ctaLink: '/produkter',
  ctaLabel: 'Se alla scen- och trossprodukter',
};

export const ROKMASKIN_KONFETTI_PRODUCTS: LandingProductSection = {
  testId: 'effekter-products',
  title: 'Populära effektmaskiner',
  subtitle: 'Rök, haze, konfetti, CO₂ och kallgnist från katalogen.',
  slugs: [
    'mellanstor-rokmaskin-med-fjarrkontroll-1000w',
    'mellan-haze',
    'konfettikanon-skjuter-konfettiror',
    'co2-jet-kraftfull-scen-effekt-for-event-klubb-brollop',
    'kallgnistpaket-2-st-kallgnistmaskiner-sparkular-effekt',
  ],
  ctaLink: '/produkter',
  ctaLabel: 'Se hela katalogen',
};

/** Complements Bas/Standard/Premium + existing Bild carousel on /foretag. */
export const FORETAG_CURATED_PRODUCTS: LandingProductSection = {
  testId: 'foretag-curated-products',
  title: 'Vanlig företagsutrustning',
  subtitle:
    'Konferens och event från katalogen – med d&b som premiumankare när produktionen kräver mer.',
  slugs: [
    'konferenspaket-konferens-workshop-utbildning-eller-foretagsevent',
    'mellan-ljudpaketet',
    'tradlos-mikrofon-duo-2-x-handhallen',
    'omgivningsbelysning-6-pack-uplights',
    'full-hd-projektor-5-200-ansi-lumen',
    'd-b-xsl8',
  ],
  ctaLink: '/produkter',
  ctaLabel: 'Se hela katalogen',
};

/** All curated slug lists used by landings (for validation tests). */
export const ALL_LANDING_PRODUCT_SECTIONS: readonly LandingProductSection[] = [
  HYRA_LJUD_STUDENTFEST_PRODUCTS,
  STUDENTFLAK_LJUDPAKET_PRODUCTS,
  JULFEST_FORETAG_PRODUCTS,
  EVENTTEKNIK_FORETAG_AUDIO_PRODUCTS,
  EVENTTEKNIK_FORETAG_VISUAL_PRODUCTS,
  EVENTTEKNIK_FESTIVAL_PRODUCTS,
  EVENTTEKNIK_MASSOR_PRODUCTS,
  LJUD_LJUS_BROLLOP_PRODUCTS,
  BROLLOP_HUB_PRODUCTS,
  LJUD_LJUS_SKANE_PRODUCTS,
  LJUD_LJUS_MALMO_PRODUCTS,
  LJUD_LJUS_LUND_PRODUCTS,
  LJUD_LJUS_HELSINGBORG_PRODUCTS,
  HYRA_SCEN_MALMO_PRODUCTS,
  SCEN_HUB_PRODUCTS,
  ROKMASKIN_KONFETTI_PRODUCTS,
  FORETAG_CURATED_PRODUCTS,
];

export function collectLandingSlugs(sections: readonly LandingProductSection[] = ALL_LANDING_PRODUCT_SECTIONS): string[] {
  return [...new Set(sections.flatMap((s) => [...s.slugs]))];
}

export function getLjudLjusCityProducts(
  citySlug: 'malmo' | 'lund' | 'helsingborg'
): LandingProductSection {
  switch (citySlug) {
    case 'malmo':
      return LJUD_LJUS_MALMO_PRODUCTS;
    case 'lund':
      return LJUD_LJUS_LUND_PRODUCTS;
    case 'helsingborg':
      return LJUD_LJUS_HELSINGBORG_PRODUCTS;
  }
}
